import classNames from "classnames";
import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { CheckIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { Button } from "@design-system/Button/Button";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import {
    COUNTERPARTY_REVIEW_FIELD_LABELS,
    GENERIC_COUNTERPARTY_REVIEW_FIELDS,
    GenericCounterpartyReviewField,
} from "@common/types/counterpartyFieldReview";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import styles from "./CounterpartyDetailsReview.module.scss";

/** A single reviewer entry for a reviewed field. */
export interface CounterpartyFieldReviewer {
    userId: number;
    name: string;
    reviewedAt: string;
    avatarUrl: string | null;
    iconColor: string;
    email: string | null;
}

/** Per-field review state for a counterparty. */
export type CounterpartyFieldReviewState = Record<
    GenericCounterpartyReviewField,
    CounterpartyFieldReviewer[]
>;

interface ReviewRowProps {
    label: string;
    reviewers: CounterpartyFieldReviewer[];
    currentUserId: number;
    onMarkReviewed?: () => void;
    onUndoReview?: () => void;
    isDisabled?: boolean;
}

const REVIEWER_DATE_FORMAT = "MMM d, yyyy, h:mmaaa";

const ReviewerAvatars: React.FC<{ reviewers: CounterpartyFieldReviewer[] }> = ({
    reviewers,
}) =>
    reviewers.length > 0 && (
        <AvatarGroup
            items={reviewers}
            getKey={(reviewer) => `${reviewer.userId}-${reviewer.reviewedAt}`}
            getLabel={(reviewer) => reviewer.name}
            renderAvatar={(reviewer) => (
                <AvatarTooltip
                    title={reviewer.name}
                    content={format(
                        parseISO(reviewer.reviewedAt),
                        REVIEWER_DATE_FORMAT,
                    )}
                >
                    <UserAvatar
                        user={{
                            name: reviewer.name,
                            avatar: reviewer.avatarUrl,
                            iconColor: reviewer.iconColor,
                            email: reviewer.email,
                        }}
                    />
                </AvatarTooltip>
            )}
            max={reviewers.length}
            size="sm"
        />
    );

export const ReviewRow: React.FC<ReviewRowProps> = ({
    label,
    reviewers,
    currentUserId,
    onMarkReviewed,
    onUndoReview,
    isDisabled,
}) => {
    const [isHighlighted, setIsHighlighted] = useState(false);
    const isReviewedByMe = reviewers.some((r) => r.userId === currentUserId);

    const markReviewedClassName = classNames(styles.markReviewedBtn, {
        [styles.markReviewedBtnMuted]: !isHighlighted,
    });

    return (
        <div
            className={styles.reviewRow}
            onMouseEnter={() => setIsHighlighted(true)}
            onMouseLeave={() => setIsHighlighted(false)}
            onFocus={() => setIsHighlighted(true)}
            onBlur={() => setIsHighlighted(false)}
        >
            <span className={styles.reviewLabel}>{label}</span>
            <HStack gap={4} align="center" justify="between">
                <HStack align="center">
                    {isReviewedByMe && isHighlighted && (
                        <Button
                            variant="primary"
                            size="sm"
                            className={styles.undoReviewBtn}
                            disabled={isDisabled}
                            onClick={onUndoReview}
                        >
                            Undo review
                        </Button>
                    )}
                    {isReviewedByMe && !isHighlighted && (
                        <span className={styles.reviewed}>
                            <CheckIcon className={styles.reviewedIcon} />
                            Reviewed
                        </span>
                    )}
                    {!isReviewedByMe && (
                        <Button
                            {...(isHighlighted
                                ? { variant: "primary", size: "sm" }
                                : { variant: "link", size: "sm" })}
                            className={markReviewedClassName}
                            disabled={isDisabled}
                            onClick={onMarkReviewed}
                        >
                            Mark reviewed
                        </Button>
                    )}
                </HStack>
                <ReviewerAvatars reviewers={reviewers} />
            </HStack>
        </div>
    );
};

interface Props {
    reviewState: CounterpartyFieldReviewState;
    currentUserId: number;
    onMarkReviewed?: (field: GenericCounterpartyReviewField) => void;
    onUndoReview?: (field: GenericCounterpartyReviewField) => void;
    children?: React.ReactNode;
}

export const CounterpartyDetailsReview: React.FC<Props> = ({
    reviewState,
    currentUserId,
    onMarkReviewed,
    onUndoReview,
    children,
}) => (
    <VStack gap={4}>
        {GENERIC_COUNTERPARTY_REVIEW_FIELDS.map((field) => (
            <ReviewRow
                key={field}
                label={COUNTERPARTY_REVIEW_FIELD_LABELS[field]}
                reviewers={reviewState[field]}
                currentUserId={currentUserId}
                onMarkReviewed={() => onMarkReviewed?.(field)}
                onUndoReview={() => onUndoReview?.(field)}
            />
        ))}
        {children}
    </VStack>
);
